import queryString from 'query-string';

import { getTitleImageOriginBaseUrl as getTitleImageOriginBaseUrl } from '@api/shared/utils';
import { cdnCacheApiUrl, imageOriginKey, imageOriginUrl } from '@config/api';
import { ImageType } from '@models/Image';
import { DetailedTitle, SimpleTitle, Title } from '@models/Title';
import { appendSearchParamsToURL } from '@utils/urlUtils';

export type ImgOptions = {
  hash?: number;
  height?: number;
  width?: number;
};

type TitleImgUrlOptions = ImgOptions & { scope?: { id: string; type: 'collection' | 'provider' } };

export const getTitleImgUrl = (
  masterId: string,
  imageType: ImageType,
  options?: TitleImgUrlOptions
) => {
  const { hash, height, width, scope: { type = '', id = '' } = {} } = options || {};

  const url = [
    getTitleImageOriginBaseUrl('titles'),
    masterId,
    ...(type === 'collection' ? [`collections/${id}`] : []),
    ...(type === 'provider' ? [`providers/${id}`] : []),
    imageType,
  ].join('/');

  const qs = queryString.stringify({
    width,
    height,
    ...(hash ? { hash } : {}),
    'api-version': '1.0',
    'subscription-key': imageOriginKey,
  });

  return `${url}?${qs}`;
};

export const getEditorialImgUrl = (path: string, imageType: ImageType, options?: ImgOptions) => {
  const { hash, height, width } = options || {};

  const url = `${imageOriginUrl}${path}/${imageType}`;

  const qs = queryString.stringify({
    width,
    height,
    ...(hash ? { hash } : {}),
    'api-version': '1.0',
    'subscription-key': imageOriginKey,
  });

  return `${url}?${qs}`;
};

export type GetTitleImageTypeUrlFn = (masterId: string, options?: TitleImgUrlOptions) => string;

export const getWideTitleImageUrl: GetTitleImageTypeUrlFn = (masterId, options) =>
  getTitleImgUrl(masterId, 'wide', options);

export const getPosterTitleImageUrl: GetTitleImageTypeUrlFn = (masterId, options) =>
  getTitleImgUrl(masterId, 'poster', options);

export const getSquareTitleImageUrl: GetTitleImageTypeUrlFn = (masterId, options) =>
  getTitleImgUrl(masterId, 'square', options);

export type PartialTitleWithProvider = Pick<Title, 'masterId'> &
  (Pick<DetailedTitle, 'playbackOptions'> | Pick<SimpleTitle, 'providers'>);

export const getTitleImageUrlWithIndexName = (
  masterId: string,
  imageType: ImageType,
  indexName?: string,
  options?: TitleImgUrlOptions
) =>
  getTitleImgUrl(masterId, imageType, {
    ...options,
    ...(indexName ? { scope: { id: indexName, type: 'provider' } } : {}),
  });

export const getTitleImageUrlFromProvider = (
  title: PartialTitleWithProvider,
  imageType: ImageType,
  provider?: string,
  options?: TitleImgUrlOptions
) =>
  getTitleImgUrl(title.masterId, imageType, {
    ...options,
    ...(provider ? { scope: { id: provider, type: 'provider' } } : {}),
  });

export interface GetTitleImageTypeUrlWithIndexFn {
  <T extends PartialTitleWithProvider>(
    title: T,
    indexName?: string,
    options?: TitleImgUrlOptions
  ): string;
}

export const getWideTitleImageUrlWithIndex: GetTitleImageTypeUrlWithIndexFn = (
  title,
  indexName,
  options
) => getTitleImageUrlFromProvider(title, 'wide', indexName, options);

export const getPosterTitleImageUrlWithIndex: GetTitleImageTypeUrlWithIndexFn = (
  title,
  indexName,
  options
) => getTitleImageUrlFromProvider(title, 'poster', indexName, options);

export const getSquareTitleImageUrlWithIndex: GetTitleImageTypeUrlWithIndexFn = (
  title,
  indexName,
  options
) => getTitleImageUrlFromProvider(title, 'square', indexName, options);

export type GetEditorialImageTypeUrlFn = (path: string, options?: ImgOptions) => string;

export const getWideEditorialImageUrl: GetEditorialImageTypeUrlFn = (path, options) =>
  getEditorialImgUrl(path, 'wide', options);

export const getPosterEditorialImageUrl: GetEditorialImageTypeUrlFn = (path, options) =>
  getEditorialImgUrl(path, 'poster', options);

export const getSquareEditorialImageUrl: GetEditorialImageTypeUrlFn = (path, options) =>
  getEditorialImgUrl(path, 'square', options);

export const getImageWidthInList = (imageType: ImageType) => {
  switch (imageType) {
    case 'poster':
      return 42;
    case 'square':
      return 48;
    default:
    case 'wide':
      return 80;
  }
};

/**
 * Helper function that appends a hash to image URLs to ensure no caching
 */
export const buildImageSrc = (url?: string, hash?: number, width?: number) =>
  url
    ? appendSearchParamsToURL(url, { hash: hash && !cdnCacheApiUrl ? hash : undefined, width })
    : '';

const _ImageAttributeKeys = [
  'isDeletable',
  'isFallback',
  'wasFound',
  'originalHeight',
  'originalWidth',
] as const;

type ImageAttribute = (typeof _ImageAttributeKeys)[number];

type ImageHeaderDefs = {
  headerKey: string;
  type: 'boolean' | 'number';
};

export const imageAttributes: Record<ImageAttribute, ImageHeaderDefs> = {
  isDeletable: { type: 'boolean', headerKey: 'x-is-deletable' },
  isFallback: { type: 'boolean', headerKey: 'x-is-fallback' },
  wasFound: { type: 'boolean', headerKey: 'x-was-found' },
  originalHeight: { type: 'number', headerKey: 'x-original-height' },
  originalWidth: { type: 'number', headerKey: 'x-original-width' },
};

const _ImageAttributeTemplate = {
  isDeletable: false as boolean,
  isFallback: false as boolean,
  wasFound: false as boolean,
  originalHeight: -1 as number,
  originalWidth: -1 as number,
} satisfies Record<ImageAttribute, boolean | number>;

export type ImageAttributes = typeof _ImageAttributeTemplate;

const transformValue = (type: 'boolean' | 'number', value: null | string): boolean | number => {
  if (!value) return -1;

  switch (type) {
    case 'boolean':
      return ['true', 'True'].includes(value);
    default:
    case 'number':
      return parseInt(value) || 0;
  }
};

export const getImageAttrFromHeader = (headers: Headers): ImageAttributes =>
  Object.entries(imageAttributes).reduce(
    (acc, [attr, { type, headerKey }]) => ({
      ...acc,
      [attr]: transformValue(type, headers.get(headerKey)),
    }),
    {} as ImageAttributes
  );
